import React from 'react';
import type {
  FilterItem,
  FilterSection as FilterSectionType,
} from '../../models/search-result';
import {
  DEFAULT_VISIBLE_FACET_VALUES,
  TREE_NSDI_SECTION_ID,
} from './FilterSidebar.constants';
import { getDefaultExpandedState } from './FilterSidebar.helpers';
import FilterItems from './FilterItems';
import FilterValueCategory from './FilterValueCategory';
import type {
  FilterSidebarExpandedState,
  FilterSidebarShowMoreState,
} from './useFilterSidebarState';

interface FilterSectionProps {
  section: FilterSectionType;
  expandedSections: FilterSidebarExpandedState;
  showMoreStates: FilterSidebarShowMoreState;
  valueCategoriesCollapsedByDefault: boolean;
  onToggleSection: (section: string) => void;
  onToggleShowMore: (section: string) => void;
  onFilterToggle: (sectionId: string, filterItem: FilterItem) => void;
  isFilterChecked: (sectionId: string, filterItem: FilterItem) => boolean;
}

const FilterSection: React.FC<FilterSectionProps> = ({
  section,
  expandedSections,
  showMoreStates,
  valueCategoriesCollapsedByDefault,
  onToggleSection,
  onToggleShowMore,
  onFilterToggle,
  isFilterChecked,
}) => {
  const isExpanded =
    expandedSections[section.id] ??
    getDefaultExpandedState(section.id, valueCategoriesCollapsedByDefault);
  const title =
    section.id === TREE_NSDI_SECTION_ID
      ? section.title
      : section.title.toUpperCase();

  return (
    <div key={section.id} className="filter-subsection">
      <button
        type="button"
        className="filter-subsection-header"
        onClick={() => onToggleSection(section.id)}
      >
        <span className={`expand-icon ${isExpanded ? 'expanded' : ''}`}>
          ▶
        </span>
        <span>{title}</span>
      </button>
      {isExpanded && (
        <div className="filter-items">
          {section.emptyMessage ? (
            <div className="filter-empty-message" role="status">
              {section.emptyMessage}
            </div>
          ) : null}
          {section.valueCategories?.map((category) => (
            <FilterValueCategory
              key={category.id}
              section={section}
              category={category}
              expandedSections={expandedSections}
              showMoreStates={showMoreStates}
              valueCategoriesCollapsedByDefault={
                valueCategoriesCollapsedByDefault
              }
              onToggleSection={onToggleSection}
              onToggleShowMore={onToggleShowMore}
              onFilterToggle={onFilterToggle}
              isFilterChecked={isFilterChecked}
            />
          ))}
          {!section.emptyMessage && section.ungroupedFilters?.length ? (
            <FilterItems
              items={section.ungroupedFilters}
              filterSectionId={section.id}
              showMoreStates={showMoreStates}
              onToggleShowMore={onToggleShowMore}
              onFilterToggle={onFilterToggle}
              isFilterChecked={isFilterChecked}
            />
          ) : !section.emptyMessage && !section.valueCategories?.length ? (
            <FilterItems
              items={section.filters}
              filterSectionId={section.id}
              showMoreStates={showMoreStates}
              onToggleShowMore={onToggleShowMore}
              onFilterToggle={onFilterToggle}
              isFilterChecked={isFilterChecked}
              limit={DEFAULT_VISIBLE_FACET_VALUES}
            />
          ) : null}
        </div>
      )}
    </div>
  );
};

export default FilterSection;
